import { INoiseInfo } from "../store/useNoiseInfo";
import { RepairIssueItem } from "../store/useRepairIssueInfo";
import { RepairSubItem } from "../interfaces/home";
import {
  shouldShowLastIssueTime,
} from "../config/constants";

/**
 * 故障维修「问题描述页」相关校验纯函数，供编辑态（RepairIssueDescription）与
 * 异响子模块（NoiseFeed）复用，避免「是否已标注 / 是否可下一步」判定在多处各写一份。
 */

/** 异响位置是否已标注（已选点或已上传合成图） */
export const isNoiseMarked = (
  noiseInfo: Pick<INoiseInfo, "xPct" | "yPct" | "positionImg">
): boolean =>
  (noiseInfo.xPct != null && noiseInfo.yPct != null) || !!noiseInfo.positionImg;

/**
 * 异响反馈是否填写完整：
 * - 位置必标注
 * - 能否重现异响必选（是/否，noiseRecurrent 不能为 null）
 */
export const isNoiseFeedbackComplete = (noiseInfo: INoiseInfo): boolean =>
  isNoiseMarked(noiseInfo) &&
  noiseInfo.noiseRecurrent != null;

/**
 * 问题描述页必填项是否全部满足（决定「下一步」是否可点）：
 * - 所有子项「问题描述」必填（含异响项的「请描述您遇到的异响问题」）
 * - 「最近一次问题时间」露出即必填（除异响103/外观内饰104 外的 code，含其他108，见 shouldShowLastIssueTime）
 * - 异响：见 isNoiseFeedbackComplete
 */
export const isIssueDescriptionComplete = (params: {
  feedbackItems: RepairSubItem[];
  getIssue: (code: number | string) => RepairIssueItem;
  isNoiseSelected: boolean;
  noiseInfo: INoiseInfo;
}): boolean => {
  const { feedbackItems, getIssue, isNoiseSelected, noiseInfo } = params;
  const itemsOk = feedbackItems.every((item) => {
    const code = Number(item.code);
    // 所有子项问题描述必填（异响项的描述框同样必填）
    if (!getIssue(item.code).desc.trim()) {
      return false;
    }
    if (
      shouldShowLastIssueTime(code) &&
      !getIssue(item.code).lastIssueTime
    ) {
      return false;
    }
    return true;
  });
  return itemsOk && (!isNoiseSelected || isNoiseFeedbackComplete(noiseInfo));
};

/**
 * 逐项校验并返回未通过字段集合，供「下一步」点击时定位标红与滚动。
 * key 格式：
 *   desc-{code}      问题描述未填写
 *   time-{code}      最近一次问题时间未选择
 *   noise-position   异响位置未标注
 *   noise-recurrent  能否重现异响未选择
 */
export const getIssueDescriptionErrors = (params: {
  feedbackItems: RepairSubItem[];
  getIssue: (code: number | string) => RepairIssueItem;
  isNoiseSelected: boolean;
  noiseInfo: INoiseInfo;
}): Set<string> => {
  const { feedbackItems, getIssue, isNoiseSelected, noiseInfo } = params;
  const errors = new Set<string>();

  feedbackItems.forEach((item) => {
    const code = Number(item.code);
    if (!getIssue(item.code).desc.trim()) {
      errors.add(`desc-${item.code}`);
    }
    if (shouldShowLastIssueTime(code) && !getIssue(item.code).lastIssueTime) {
      errors.add(`time-${item.code}`);
    }
  });

  if (isNoiseSelected) {
    if (!isNoiseMarked(noiseInfo)) {
      errors.add("noise-position");
    }
    if (noiseInfo.noiseRecurrent == null) {
      errors.add("noise-recurrent");
    }
  }

  return errors;
};
